/* ==========================================================================
   Quas health page
   --------------------------------------------------------------------------
   Two small enhancements for the health page. Both only set state: every
   figure is complete and readable before this script runs, and without it.

   [data-quas-health-lens]
     The hero's microscope key. Hovering, focusing or pressing a key item,
     or hovering its numbered badge on the lens, sets data-lens-active on
     the root, which picks that kind of element out in the illustration. While the lens is on screen, and the visitor
     has not asked for reduced motion, the key tours itself every few
     seconds until the visitor takes over.

   [data-quas-health-journey]
     The gut journey. As each step crosses the middle of the viewport it
     becomes active: the root gets data-active (the step number) and
     data-region (the part of the gut the step is about), and the line of
     fibre's route extends to that step's stop, with a bead at its head.
     With reduced motion the journey stays as authored: every part named
     and the route drawn in full.
   ========================================================================== */

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ lens key */

  function setupLens(root) {
    const keys = [...root.querySelectorAll('[data-quas-lens-key]')];
    if (!keys.length) return;

    let pinned = null;
    let hovered = null;
    let touring = null;
    let tourIndex = -1;
    let timer = 0;

    const render = () => {
      const active = pinned || hovered || touring;
      if (active) root.dataset.lensActive = active;
      else delete root.dataset.lensActive;
      keys.forEach((key) => {
        key.classList.toggle('is-active', key.dataset.quasLensKey === active);
        key.setAttribute('aria-pressed', key.dataset.quasLensKey === pinned ? 'true' : 'false');
      });
    };

    const stopTour = () => {
      window.clearInterval(timer);
      timer = 0;
      touring = null;
    };

    const startTour = () => {
      if (timer || pinned || reducedMotion.matches) return;
      timer = window.setInterval(() => {
        tourIndex = (tourIndex + 1) % keys.length;
        touring = keys[tourIndex].dataset.quasLensKey;
        render();
      }, 2600);
    };

    keys.forEach((key) => {
      const name = key.dataset.quasLensKey;
      const enter = () => {
        stopTour();
        hovered = name;
        render();
      };
      const leave = () => {
        hovered = null;
        render();
      };
      key.addEventListener('pointerenter', enter);
      key.addEventListener('focus', enter);
      key.addEventListener('pointerleave', leave);
      key.addEventListener('blur', leave);
      key.addEventListener('click', () => {
        stopTour();
        pinned = pinned === name ? null : name;
        render();
      });
    });

    // The badges on the rim answer a pointer the same way as the key.
    root.querySelectorAll('[data-quas-lens-marker]').forEach((marker) => {
      marker.addEventListener('pointerenter', () => {
        stopTour();
        hovered = marker.dataset.quasLensMarker;
        render();
      });
      marker.addEventListener('pointerleave', () => {
        hovered = null;
        render();
      });
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !hovered) startTour();
            else if (!entry.isIntersecting) {
              stopTour();
              render();
            }
          }
        },
        { threshold: 0.5 }
      ).observe(root.querySelector('.quas-health-lens') || root);
    }
  }

  /* ------------------------------------------------------------- journey */

  function setupJourney(root) {
    const steps = [...root.querySelectorAll('[data-quas-health-step]')];
    const route = root.querySelector('.quas-health-gut__route');
    const bead = root.querySelector('.quas-health-gut__bead');
    if (!steps.length || !route || typeof route.getTotalLength !== 'function') return;
    if (reducedMotion.matches || root.classList.contains('is-scrolly')) return;

    const total = route.getTotalLength();
    const samples = [];
    for (let at = 0; at <= total; at += 3) {
      const point = route.getPointAtLength(at);
      samples.push({ at, x: point.x, y: point.y });
    }

    // Each step stops the line at the route point nearest its marker.
    const stops = steps.map((step) => {
      const marker = root.querySelector(`[data-quas-health-stop="${step.dataset.region}"]`);
      if (!marker) return total;
      const x = Number(marker.getAttribute('cx'));
      const y = Number(marker.getAttribute('cy'));
      let best = samples[samples.length - 1];
      let bestDistance = Infinity;
      for (const sample of samples) {
        const distance = (sample.x - x) ** 2 + (sample.y - y) ** 2;
        if (distance < bestDistance) {
          bestDistance = distance;
          best = sample;
        }
      }
      return step.dataset.region === 'wall' ? total : best.at;
    });

    route.style.strokeDasharray = `${total} ${total}`;
    root.classList.add('is-scrolly');

    let shown = 0;
    let frame = 0;

    const draw = (length) => {
      shown = length;
      route.style.strokeDashoffset = String(total - length);
      if (bead) {
        const point = route.getPointAtLength(Math.max(0, Math.min(total, length)));
        bead.setAttribute('cx', point.x.toFixed(1));
        bead.setAttribute('cy', point.y.toFixed(1));
      }
    };

    const drawTo = (target) => {
      window.cancelAnimationFrame(frame);
      const from = shown;
      const duration = Math.min(1600, 500 + Math.abs(target - from) * 1.4);
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
        draw(from + (target - from) * eased);
        if (t < 1) frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    };

    // The rail beside the steps runs from the first step's number to the
    // last, and fills to the active one. offsetTop ignores the cards'
    // entrance transforms, so the rail is measured where the cards settle.
    const list = root.querySelector('.quas-health-steps');
    const centre = (step) => {
      const badge = step.querySelector('.quas-health-step__number');
      return step.offsetTop + (badge ? badge.offsetTop + badge.offsetHeight / 2 : 0);
    };
    let active = -1;
    const measureRail = () => {
      if (!list) return;
      const first = centre(steps[0]);
      list.style.setProperty('--quas-rail-start', `${first}px`);
      list.style.setProperty('--quas-rail-length', `${centre(steps[steps.length - 1]) - first}px`);
      list.style.setProperty('--quas-rail-at', `${active < 0 ? 0 : centre(steps[active]) - first}px`);
    };
    if (list && 'ResizeObserver' in window) new ResizeObserver(measureRail).observe(list);

    const activate = (index) => {
      if (index === active) return;
      active = index;
      measureRail();
      root.dataset.active = String(index + 1);
      root.dataset.region = steps[index].dataset.region;
      steps.forEach((step, i) => {
        step.classList.toggle('is-active', i === index);
        step.classList.toggle('is-past', i < index);
      });
      drawTo(stops[index]);
    };

    draw(0);
    measureRail();

    if (!('IntersectionObserver' in window)) {
      activate(steps.length - 1);
      return;
    }

    // A thin band across the middle of the viewport picks the active step.
    // On one column the drawing is pinned over the top of the screen, so
    // the band sits in the middle of the space left below it.
    const stepObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) activate(steps.indexOf(entry.target));
        }
      },
      { rootMargin: window.matchMedia('(max-width: 989px)').matches ? '-60% 0px -32% 0px' : '-46% 0px -46% 0px' }
    );
    steps.forEach((step) => stepObserver.observe(step));

    // Arriving at the journey starts the first step, even before its card
    // reaches the middle of the screen.
    const arrival = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && active < 0) {
          activate(0);
          arrival.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    arrival.observe(root.querySelector('.quas-health-journey__figure') || root);
  }

  const init = () => {
    document.querySelectorAll('[data-quas-health-lens]').forEach(setupLens);
    document.querySelectorAll('[data-quas-health-journey]').forEach(setupJourney);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-quas-health-lens]').forEach(setupLens);
    event.target.querySelectorAll('[data-quas-health-journey]').forEach(setupJourney);
  });
})();
